import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { Typography } from '@material-ui/core'
import { requestBPDetailsRefetch } from 'api/graphQL/reactiveVars/workspaceToggles'
import { useBestPracticesMutation } from 'components/WorkspaceEditor/api/useBestPracticesMutation'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { useWorkspace2Store } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useCallback, useEffect, useState } from 'react'
import { revertBPPreviewAndDeselect } from '../hooks/useApplyBPFromConfig'
import { useBestPracticeAuditHistory } from '../hooks/useBestPracticeAuditHistory'
import { useBestPracticeManagementStore } from '../store/BestPracticeManagementStore'
import { useBestPracticeManagementPanelStyles } from '../styles/useBestPracticeManagementPanelStyles'
import { BestPracticeTip } from '../types/bpManagement.types'
import { updateBestPracticesForContentItem } from '../utils/bestPracticesUtils'
import { BestPracticeTipCard } from './BestPracticeTipCard'
import { BestPracticeTipCardSkeleton } from './BestPracticeTipCardSkeleton'

const SKELETON_COUNT = 3

const STATUS_MESSAGES = {
    ERROR: 'Failed to load Best Practice activity. Please try again.',
    WAITING: 'Select a section version to see its Best Practice activity.',
    EMPTY: 'No Best Practice activity yet for this section.',
}

interface BestPracticeTipsListProps {
    onTipSelect?: (tip: BestPracticeTip | null) => void
}

export const BestPracticeTipsList = ({
    onTipSelect,
}: BestPracticeTipsListProps) => {
    const classes = useBestPracticeManagementPanelStyles()
    const {
        tips,
        loading,
        error,
        waitingForIteration,
        refetch,
    } = useBestPracticeAuditHistory()
    const { removeBestPractice } = useBestPracticesMutation()
    const [isDeleting, setIsDeleting] = useState(false)
    const activeTipId = useBestPracticeManagementStore((s) => s.activeTipId)
    const setActiveTip = useBestPracticeManagementStore((s) => s.setActiveTip)
    const auditContentItemFilter = useBestPracticeManagementStore(
        (s) => s.auditContentItemFilter
    )
    const notify = useNotification()

    useEffect(() => () => setActiveTip(null, null), [setActiveTip])

    const handleCardClick = useCallback(
        (tip: BestPracticeTip) => {
            if (!auditContentItemFilter) {
                notify.info({
                    message:
                        'Click on a Best Practice icon in the workspace to filter and preview versions for a specific content item.',
                })
                return
            }

            // Deselect: clicking active card resets the panel and clears preview
            if (activeTipId === tip.id) {
                setActiveTip(null, null)
                onTipSelect?.(null)
                return
            }

            const latestVersion = tip.version
            if (!latestVersion) return

            const {
                previewSnapshot,
                setPreviewSnapshot,
            } = useBestPracticeManagementStore.getState()
            let nodes = useWorkspace2Store.getState().contentItemViewNodes
            const primaryMatch = nodes.find((n) => n.id === tip.contentItemId)
            const targetNode =
                primaryMatch ??
                nodes.find((n) => n.id === tip.appliedContentItemId)
            if (!targetNode) return

            const effectiveContentItemId =
                primaryMatch != null
                    ? tip.contentItemId!
                    : tip.appliedContentItemId!

            // Restore previous preview if it was for a different content item
            if (
                previewSnapshot &&
                previewSnapshot.contentItemId !== effectiveContentItemId
            ) {
                nodes = nodes.map((n) =>
                    n.id === previewSnapshot.contentItemId
                        ? { ...n, elements: previewSnapshot.originalElements }
                        : n
                )
                setPreviewSnapshot(null)
            }

            if (
                !previewSnapshot ||
                previewSnapshot.contentItemId !== effectiveContentItemId
            ) {
                setPreviewSnapshot({
                    contentItemId: effectiveContentItemId,
                    originalElements: targetNode.elements ?? [],
                })
            }

            const versionElements = ((latestVersion.elements ??
                []) as unknown) as Partial<TextContentElement>[]
            useWorkspace2Store
                .getState()
                .actions.setContentItemViewNodes(
                    nodes.map((n) =>
                        n.id === effectiveContentItemId
                            ? { ...n, elements: versionElements }
                            : n
                    )
                )
            setActiveTip(tip.id, tip.bestPracticeId, effectiveContentItemId)
            onTipSelect?.(tip)
        },
        [activeTipId, auditContentItemFilter, notify, setActiveTip, onTipSelect]
    )

    if (loading || isDeleting) {
        return (
            <div data-testid="bestPracticeTipsListLoading">
                {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                    <BestPracticeTipCardSkeleton key={`skeleton-${i}`} />
                ))}
            </div>
        )
    }

    const statusMessage = error
        ? STATUS_MESSAGES.ERROR
        : waitingForIteration
        ? STATUS_MESSAGES.WAITING
        : tips.length === 0
        ? STATUS_MESSAGES.EMPTY
        : null

    if (statusMessage) {
        return (
            <Typography className={classes.headerText}>
                {statusMessage}
            </Typography>
        )
    }

    return (
        <div data-testid="bestPracticeTipsList">
            {tips.map((tip) => (
                <BestPracticeTipCard
                    key={tip.id}
                    tip={tip}
                    isActive={activeTipId === tip.id}
                    onClick={() => handleCardClick(tip)}
                    onDelete={async () => {
                        setIsDeleting(true)
                        try {
                            await removeBestPractice({
                                variables: {
                                    input: { id: tip.bestPracticeId },
                                },
                            })
                            const {
                                contentItemViewNodes,
                                actions,
                            } = useWorkspace2Store.getState()
                            const primaryMatch = contentItemViewNodes.find(
                                (n) => n.id === tip.contentItemId
                            )
                            const appliedMatch =
                                !primaryMatch &&
                                contentItemViewNodes.find(
                                    (n) => n.id === tip.appliedContentItemId
                                )
                            const effectiveId = primaryMatch
                                ? tip.contentItemId!
                                : appliedMatch
                                ? tip.appliedContentItemId!
                                : (tip.contentItemId ?? tip.appliedContentItemId)
                            if (effectiveId) {
                                const remainingCount = new Set(
                                    tips
                                        .filter(
                                            (t) =>
                                                t.contentItemId ===
                                                    tip.contentItemId &&
                                                t.bestPracticeId !==
                                                    tip.bestPracticeId &&
                                                t.action !== 'REMOVE'
                                        )
                                        .map((t) => t.bestPracticeId)
                                ).size

                                if (remainingCount === 0) {
                                    actions.setContentItemViewNodes(
                                        updateBestPracticesForContentItem(
                                            effectiveId,
                                            undefined,
                                            contentItemViewNodes,
                                            false,
                                            false
                                        )
                                    )
                                }
                                requestBPDetailsRefetch(effectiveId)
                            }
                            revertBPPreviewAndDeselect()
                            onTipSelect?.(null)
                            refetch()
                        } finally {
                            setIsDeleting(false)
                        }
                    }}
                />
            ))}
        </div>
    )
}
